import { useEffect, useState } from "react";
import { QrCode, X, ChevronLeft, ChevronRight } from "lucide-react";
import moment from "moment";

const STATUS_NOTE = {
  checked_in: "Used",
  void: "Cancelled",
};

/** QR tickets for one order on the My Tickets page, with a full-screen view for the door. */
export default function OrderQrTickets({ tickets }) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(null);

  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => Math.min(i + 1, tickets.length - 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, tickets.length]);

  if (!tickets?.length) return null;
  const active = index !== null ? tickets[index] : null;

  return (
    <div className="pt-4 border-t border-[#1F2937]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white text-black text-xs font-black uppercase hover:bg-white/90 transition-colors"
      >
        <QrCode className="w-3.5 h-3.5" />
        {open ? "Hide" : "Show"} QR {tickets.length === 1 ? "ticket" : `tickets (${tickets.length})`}
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
          {tickets.map((t, i) => (
            <button
              key={t.id}
              type="button"
              disabled={!t.qr}
              onClick={() => setIndex(i)}
              className="relative rounded-xl bg-white p-3 text-left disabled:opacity-40"
              aria-label={`Open ticket ${t.ticketCode} full screen`}
            >
              {t.qr ? (
                <img src={t.qr} alt="" className={`w-full aspect-square ${t.status !== "issued" ? "opacity-30" : ""}`} />
              ) : (
                <div className="w-full aspect-square flex items-center justify-center text-black/40 text-xs">No QR</div>
              )}
              <p className="text-[11px] font-mono font-bold text-black mt-2 truncate">{t.ticketCode}</p>
              <p className="text-[11px] text-black/60 truncate">{t.ticketTypeName}</p>
              {STATUS_NOTE[t.status] && (
                <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black text-white text-[10px] font-bold uppercase">
                  {STATUS_NOTE[t.status]}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-[2147483000] bg-white flex flex-col items-center justify-center p-6 text-black" role="dialog" aria-modal="true">
          <button type="button" onClick={() => setIndex(null)} className="absolute top-4 right-4 p-2 rounded-full bg-black/5" aria-label="Close">
            <X className="w-6 h-6" />
          </button>
          <p className="text-xs uppercase tracking-widest text-black/50 font-bold">
            Ticket {index + 1} of {tickets.length}
          </p>
          <p className="text-lg font-black text-center mt-1">{active.eventName}</p>
          <p className="text-sm text-black/60">{active.ticketTypeName}</p>
          <img src={active.qr} alt={`QR code for ticket ${active.ticketCode}`} className="w-full max-w-[340px] aspect-square mt-6" />
          <p className="text-xl font-mono font-black tracking-widest mt-4">{active.ticketCode}</p>
          {active.status === "checked_in" && active.checkedInAt && (
            <p className="text-sm text-black/60 mt-2">Used {moment(active.checkedInAt).format("MMM D, h:mm A")}</p>
          )}
          <p className="text-xs text-black/40 mt-6 text-center max-w-xs">Turn your screen brightness up. Each code admits one person once.</p>
          {tickets.length > 1 && (
            <div className="flex gap-4 mt-6">
              <button type="button" disabled={index === 0} onClick={() => setIndex(index - 1)} className="p-3 rounded-full bg-black/5 disabled:opacity-30" aria-label="Previous ticket">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                disabled={index === tickets.length - 1}
                onClick={() => setIndex(index + 1)}
                className="p-3 rounded-full bg-black/5 disabled:opacity-30"
                aria-label="Next ticket"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
